import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { useCurrentApplication } from '../useCurrentApplication'

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(256, 'Name cannot exceed 256 characters'),
  description: z.string().max(256, 'Description cannot exceed 256 characters').optional(),
})

export type ApplicationRoleForm = z.infer<typeof formSchema>

type MessagesApplicationsApplicationRole = RestSchemas['Messages.Applications.ApplicationRole']
type MessagesApplicationsNewApplicationRole = RestSchemas['Messages.Applications.NewApplicationRole']

export type ApplicationRoleItem = {
  id?: number
  applicationId?: number
  name: { value: string }
  description: { value: string }
}

type ApplicationRoleFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedAppRole?: ApplicationRoleItem
  // appId?: number
}

export const ApplicationRoleForm = (props: ApplicationRoleFormProps) => {
  const { applicationId: appId } = useCurrentApplication()
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedAppRole
  const {
    postApplicationsApiUpdateApplicationRole,
    putApplicationsApiAddApplicationRole,
    getApplicationsApiListApplicationRoles,
  } = useRestClient()

  const { mutate: updateAppRole } = postApplicationsApiUpdateApplicationRole.useMutation(undefined, {
    onSuccess: () => {
      invalidateApplicationRoles()
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Application role successfully updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error updating application role')
    },
  })

  const { mutate: addAppRole } = putApplicationsApiAddApplicationRole.useMutation(undefined, {
    onSuccess: () => {
      invalidateApplicationRoles()
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Application role successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding application role')
    },
  })

  const invalidateApplicationRoles = () => {
    getApplicationsApiListApplicationRoles.invalidateQueries({
      parameters: {
        query: {
          applicationId: appId,
        },
      },
    })
  }

  const submitHandler = useCallback(
    (data: ApplicationRoleForm) => {
      if (edit && props.selectedAppRole) {
        const updatedRole: MessagesApplicationsApplicationRole = {
          id: props.selectedAppRole.id!,
          applicationId: props.selectedAppRole.applicationId!,
          teamCount: 0,
          memberCount: 0,
          name: data.name,
          description: data.description || '',
        }

        updateAppRole({
          body: updatedRole,
        })
      } else {
        const newAppRole: MessagesApplicationsNewApplicationRole = {
          applicationId: appId!,
          name: data.name,
          description: data.description || '',
        }

        addAppRole({
          body: newAppRole,
        })
      }
    },
    [edit, props.selectedAppRole, appId, addAppRole, updateAppRole]
  )

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (props: ApplicationRoleFormProps & { submitHandler: (data: ApplicationRoleForm) => void }) => {
  const selectedAppRole: Partial<ApplicationRoleItem> = props.selectedAppRole ?? {}
  const { name, description } = selectedAppRole

  const defaultValues = useMemo(() => {
    return {
      name: name?.value ?? '',
      description: description?.value ?? '',
    }
  }, [name, description])

  const form = useForm<ApplicationRoleForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="form-action-container">
              <Button type="submit">Submit</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
